import type { EntryPhase } from "@seagames/contracts";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import mascot from "../../assets/brand/dashboard-mascot-wave.webp";
import { CheckIcon } from "../accreditation/icons";
import { type StepInfo, StatePill, WindowLine } from "./parts";
import "./entries-play.css";

/** What the NOC is doing on this page right now — picks the mascot's one-liner. */
export type JourneyMode = "edit" | "final" | "readOnly" | "waiting";

const LINE = {
	EOI: { edit: "journey.eoiEdit", final: "journey.eoiFinal", readOnly: "journey.eoiReadOnly", waiting: "journey.eoiEdit" },
	EBN: { edit: "journey.ebnEdit", final: "journey.ebnFinal", readOnly: "journey.ebnReadOnly", waiting: "journey.ebnWaiting" },
} as const;

/**
 * The top of a NOC's entries page: the mascot with one encouraging line, and
 * Sector 1's two steps EOI → EBN as a short connected track (the Shortlist
 * is Sector 2's, user 2026-10-29, so it is not a step here) — each
 * with its state and window (viewer-local, via WindowLine). The current
 * step is marked; EBN is dimmed until the EOI is submitted. `aside` holds
 * the Statuses guide button.
 */
export function JourneyHeader({
	current,
	mode,
	orgName,
	eoi,
	ebn,
	search,
	aside,
}: {
	current: EntryPhase;
	mode: JourneyMode;
	orgName: string | undefined;
	eoi: StepInfo | undefined;
	ebn: StepInfo | undefined;
	search: { games_id?: string; org?: string };
	aside?: ReactNode;
}) {
	const { t } = useTranslation("entries");
	const ebnLocked = eoi !== undefined && eoi.state !== "FINAL";
	const step = (phase: EntryPhase, n: number, info: StepInfo | undefined, disabled = false) => (
		<li className="ent-jstep" data-state={info?.state} data-current={current === phase || undefined} data-disabled={disabled || undefined}>
			<Link
				to={phase === "EOI" ? "/accreditation/entries/eoi" : "/accreditation/entries/ebn"}
				search={{ games_id: search.games_id, org: search.org }}
				aria-current={current === phase ? "step" : undefined}
				className="ent-jstep__link"
			>
				<span className="ent-jstep__dot" aria-hidden>
					{info?.state === "FINAL" ? <CheckIcon className="size-[0.75rem]" /> : n}
				</span>
				<span className="grid min-w-0 gap-[0.125rem]">
					<span className="flex flex-wrap items-center gap-x-[0.375rem] gap-y-[0.125rem]">
						<b className="text-[0.8125rem] leading-tight font-semibold text-gms-navy-ink">{t(`phase.${phase}`)}</b>
						{info ? <StatePill state={info.state} /> : <span className="skeleton h-[1.25rem] w-[4rem] rounded-[0.25rem]" />}
					</span>
					<span className="text-[0.71875rem] text-gms-ink-3">{info ? <WindowLine window={info.window} /> : " "}</span>
				</span>
			</Link>
		</li>
	);
	return (
		<section className="ent-journey" aria-label={t("strip.label")}>
			<img src={mascot} alt="" width={372} height={560} decoding="async" className="ent-journey__mascot" />
			<div className="grid min-w-0 gap-[0.625rem]">
				<div className="flex flex-wrap items-start justify-between gap-[0.5rem]">
					<p className="min-w-0 flex-[1_1_16rem] text-[0.9375rem] leading-snug font-semibold text-gms-navy-ink">
						{orgName ? t(LINE[current][mode], { org: orgName }) : <span className="skeleton inline-block h-[1.125rem] w-[16rem] rounded" />}
					</p>
					{aside}
				</div>
				<ol className="ent-jsteps">
					{step("EOI", 1, eoi)}
					{step("EBN", 2, ebn, ebnLocked)}
				</ol>
			</div>
		</section>
	);
}
